import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import gamesService from '../../services/gamesService';
import TicketCard from './TicketCard/TicketCard';

// Custom hooks
import { useActivatedPacks } from '../../hooks/useActivatedPacks';
import { useGames } from '../../hooks/useGames';
import { usePayouts } from '../../hooks/usePayouts';
import { useLottoSales } from '../../hooks/useLottoSales';

// Components
import { ActivatedPacksHeader } from './ActivatedPacksHeader';
import { StoreInfo } from './StoreInfo';
import { ActionButtons } from './ActionButtons';
import { CreatePackSection } from './CreatePackSection';
import { PayoutDialog } from './PayoutDialog';
import { LottoSaleDialog } from './LottoSaleDialog';
import type { ScannerConfig } from '../../types/features/games';

const ActivatedPacks: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const [createPackLoading, setCreatePackLoading] = useState(false);

  // Custom hooks
  const {
    packsData,
    storeData,
    processedPacks,
    loading,
    error,
    setError,
    loadStoreAndPacks,
    addNewPack,
    removePack,
  } = useActivatedPacks({ storeId: storeId || '' });

  const {
    games,
    selectedGame,
    loading: gamesLoading,
    loadGames,
    handleGameChange,
    getSelectedGameData,
  } = useGames();

  const {
    payoutDialogOpen,
    payoutAmount,
    setPayoutAmount,
    posAmount,
    setPosAmount,
    selectedPayoutType,
    selectedPayoutPack,
    lotteryPayoutLoading,
    scratcherPayoutLoading,
    handleLotteryPayout,
    handlePayoutSubmit,
    handlePayoutCancel,
  } = usePayouts({ storeId: storeId || '', storeData, setError });

  const {
    lottoSaleDialogOpen,
    lottoSaleAmount,
    setLottoSaleAmount,
    lottoSaleLoading,
    handleLottoSale,
    handleLottoSaleSubmit,
    handleLottoSaleCancel,
  } = useLottoSales({ storeId: storeId || '', storeData, setError });

  const scannerConfig: ScannerConfig | null = packsData
    ? {
        barcode_format: packsData.barcode_format,
        barcode_length: packsData.barcode_length,
        barcode_scanner_formats: packsData.barcode_scanner_formats,
      }
    : null;

  // Load data on component mount
  useEffect(() => {
    if (storeId) {
      loadStoreAndPacks();
      loadGames();
    }
  }, [storeId, loadStoreAndPacks, loadGames]);

  // Handle pack creation
  const handleCreatePack = useCallback(async () => {
    if (!selectedGame || !packsData || !storeId) return;

    const selectedGameData = getSelectedGameData();
    if (!selectedGameData) return;

    setCreatePackLoading(true);
    setError(null);

    try {
      // Convert game to pack format with default scratchers pack type
      const newPack = gamesService.convertGameToPack(selectedGameData, 'scratchers', {
        barcodeFormat: packsData?.barcode_format,
        direction: packsData?.direction,
        regionStartingTicketNumber: packsData.region_starting_ticket_number,
      });

      // Get the actual store_key from storeData or use storeId as fallback
      const actualStoreKey = storeData?.store_key || storeId;

      // Call the activate pack API
      const activateRequest = {
        store_key: actualStoreKey,
        pack_id: newPack.pack_id,
        game_id: selectedGameData.game_id.toString(),
        activated_on: newPack.date_activated,
        state: selectedGameData.state,
        pack_type: 'scratchers',
      };

      const response = await gamesService.activatePack(activateRequest);

      if (response.success) {
        // Add new pack to the existing packs
        addNewPack(newPack);
      } else {
        setError(`Failed to activate pack: ${response.message}`);
      }
    } catch (error) {
      console.error('Error creating pack:', error);
      setError('Failed to create pack. Please try again.');
    } finally {
      setCreatePackLoading(false);
    }
  }, [selectedGame, packsData, storeId, getSelectedGameData, storeData, setError, addNewPack]);

  // Handle pack return
  const handlePackReturn = useCallback((packId: string) => {
    removePack(packId);
  }, [removePack]);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ px: 2 }}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={60} />
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ px: 2 }}>
        <Box sx={{ py: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <ActivatedPacksHeader totalPacks={packsData?.total || 0} />

      <Box sx={{ px: 2, py: 4 }}>
        <Stack direction="row" justifyContent="space-between">
          <StoreInfo 
            storeData={storeData} 
            totalPacks={packsData?.total || 0} 
          />
          
          <ActionButtons
            packs={processedPacks}
            lotteryPayoutLoading={lotteryPayoutLoading}
            scratcherPayoutLoading={scratcherPayoutLoading}
            lottoSaleLoading={lottoSaleLoading}
            onLotteryPayout={(type) => handleLotteryPayout(type, processedPacks)}
            onLottoSale={handleLottoSale}
          />
        </Stack>

        <CreatePackSection
          games={games}
          selectedGame={selectedGame}
          gamesLoading={gamesLoading}
          createPackLoading={createPackLoading}
          onGameChange={handleGameChange}
          onCreatePack={handleCreatePack}
        />

        <Grid container spacing={3}>
          {processedPacks.map((pack) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={pack.pack_id}>
              <TicketCard 
                pack={pack} 
                onPackReturn={handlePackReturn} 
                storeId={storeId || ''} 
                scannerConfig={scannerConfig}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Payout Dialog */}
      <PayoutDialog
        open={payoutDialogOpen}
        payoutType={selectedPayoutType}
        selectedPack={selectedPayoutPack}
        payoutAmount={payoutAmount}
        posAmount={posAmount}
        loading={selectedPayoutType === 'lotto' ? lotteryPayoutLoading : scratcherPayoutLoading}
        onPayoutAmountChange={setPayoutAmount}
        onPosAmountChange={setPosAmount}
        onSubmit={handlePayoutSubmit}
        onCancel={handlePayoutCancel}
      />

      {/* Lotto Sale Dialog */}
      <LottoSaleDialog
        open={lottoSaleDialogOpen}
        amount={lottoSaleAmount}
        loading={lottoSaleLoading}
        onAmountChange={setLottoSaleAmount}
        onSubmit={handleLottoSaleSubmit}
        onCancel={handleLottoSaleCancel}
      />
    </Box>
  );
};

export default ActivatedPacks; 
